import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, sum } from 'lodash';
import classNames from 'classnames';
import {
    blue_600,
    blue_700,
    business_1100,
    grey_700,
    orange_1000,
    orange_800,
    pink_1000,
    pink_900,
    purple_600,
    purple_800,
    red_600,
    red_900,
    teal_1000,
    teal_900,
    yellow_1000,
    yellow_1100
} from '@fiverr-private/sass/helpers';
import { POSITIONS, SHAPES } from './constants';
import IconContainer from './IconContainer';
import OnlineIndicator from './OnlineIndicator';
import classes from './index.module.scss';

const LOADING_MODE_EAGER = 'eager';
const LOADING_MODE_LAZY = 'lazy';
export const supportedBackgroundColors = [blue_600, blue_700, red_600, red_900, orange_800, orange_1000, teal_900, teal_1000, pink_900, pink_1000, purple_600, purple_800, yellow_1000, yellow_1100];

/**
 * @deprecated Please use Avatar from @fiverr-private/avatar instead.
 */
const Avatar = ({
    src,
    className,
    size,
    shape,
    showTitle,
    showRing,
    showOnlineIndicator,
    onlineIndicatorSize,
    onlineIndicatorPosition = POSITIONS.BOTTOM_RIGHT,
    lazyLoad,
    fullName,
    username,
    color,
    icon,
    iconPosition,
    showIconRing,
    imageClassName,
    captionClassName,
    containerClassName,
    autoColor,
    isOnline,
    onClick
}) => {
    const [isImageBroken, setIsImageBroken] = useState(false);

    const onImageError = useCallback(() => {
        setIsImageBroken(true);
    }, []);

    const avatarBackgroundColor = useMemo(() => {
        if (color) {
            return color;
        }

        if (autoColor && username) {
            // We convert the long hash string to a number so that we can divide it by the available options
            const usernameInAscii = sum(username.split('').map((char) => char.charCodeAt()));

            // Taking the reminder from the division of the two numbers will give us an index inside the list range
            const colorIndex = usernameInAscii % supportedBackgroundColors.length;

            return supportedBackgroundColors[colorIndex];
        }

        return grey_700;
    }, [color, autoColor, username]);

    const ringSize = useMemo(() =>
        Math.round(size * 0.05) // The ring needs to be 1/20 of the size of the avatar
    , [size]);

    const avatarSize = useMemo(() =>
        // We need to subtract the ring width from the four sides of the avatar container to get the correct size.
        showRing ? size - ringSize * 4 : size
    , [showRing, ringSize, size]);

    const ringStyle = useMemo(() => {
        const ROUNDED_FULL = '50%';
        const ROUNDED = '4px';

        const style = {
            borderRadius: shape === SHAPES.SQUARE ? ROUNDED : ROUNDED_FULL
        };

        if (showRing) {
            style.border = `${ringSize}px solid ${business_1100}`; // This is the actual ring
            style.padding = `${ringSize}px`; // push away from the avatar itself to create an illusion of a ring
        }

        return style;
    }, [shape, showRing, ringSize]);

    const isOnClickValid = typeof onClick === 'function';
    const ElementTag = isOnClickValid ? 'button' : 'div';
    const ElementProps = isOnClickValid ? { onClick } : {};

    const figureClasses = classNames(
        classes.avatar,
        classes[`avatar-shape-${shape}`],
        className
    );

    const imageClasses = classNames(
        classes.avatarImage,
        imageClassName
    );

    const captionClasses = classNames(
        classes.avatarCaption,
        captionClassName
    );

    const avatarStyle = {
        fontSize: avatarSize,
        backgroundColor: avatarBackgroundColor
    };

    const nameValue = fullName || username;
    const firstLetter = get(nameValue, '[0]', '');

    const showImage = src && !isImageBroken;

    return (
        <ElementTag className={classNames(classes.ring, containerClassName)} style={ringStyle} {...ElementProps}>
            <figure style={avatarStyle} className={figureClasses} title={showTitle ? nameValue : ''}>
                <figcaption className={captionClasses}>{firstLetter}</figcaption>
                {showImage ?
                    <img className={imageClasses}
                        src={src}
                        alt={nameValue}
                        loading={lazyLoad ? LOADING_MODE_LAZY : LOADING_MODE_EAGER}
                        onError={onImageError}/> :
                    null}
            </figure>
            {showOnlineIndicator && <OnlineIndicator indicatorSize={onlineIndicatorSize} position={onlineIndicatorPosition} avatarSize={avatarSize} isOnline={isOnline}/>}
            {icon && <IconContainer showIconRing={showIconRing} position={iconPosition}>{icon}</IconContainer>}
        </ElementTag>
    );
};

Avatar.displayName = 'Avatar';

Avatar.propTypes = {
    /**
     * The user's full name.
     */
    fullName: PropTypes.string,
    /**
     * The user's username.
     */
    username: PropTypes.string.isRequired,
    /**
     * The image's source.
     */
    src: PropTypes.string,
    /**
     * The size of the avatar, in pixels.
     */
    size: PropTypes.number,
    /**
     * The shape of the avatar.
     * Supported options are exposed through the static `SHAPES` property.
     */
    shape: PropTypes.oneOf(Object.values(SHAPES)),
    /**
     * Whether to show the username as a title when hovering the image.
     */
    showTitle: PropTypes.bool,
    /**
     * Icon component to be shown over the avatar.
     */
    icon: PropTypes.node,
    /**
     * Icon position in case icon is passed. one of `ICON_POSITIONS`, see Static Properties below
     */
    iconPosition: PropTypes.oneOf(Object.values(POSITIONS)),
    /**
     * by default the icon is wrapped by white ring, this will hide the ring
     */
    showIconRing: PropTypes.bool,
    /**
     * Whether to show a ring around the avatar.
     */
    showRing: PropTypes.bool,
    /**
     * Whether to show a user status indicator.
     */
    showOnlineIndicator: PropTypes.bool,
    /**
     * Size of the online indicator
     */
    onlineIndicatorSize: PropTypes.number,
    /**
     * An indication that the user is logged in (only visible if `showOnlineIndicator` is set to `true`).
     */
    isOnline: PropTypes.bool,
    /**
     * Where to position the indicator on the avatar.
     */
    onlineIndicatorPosition: PropTypes.oneOf(Object.values(POSITIONS)),
    /**
     * Whether to render the image only when it reaches a close distance from the viewport.
     */
    lazyLoad: PropTypes.bool,
    /**
     * The avatar's background color. The letter's color will be white if passed.
     */
    color: PropTypes.string,
    /**
     * Adds a random background color to the avatar based on the username.
     */
    autoColor: PropTypes.bool,
    /**
     * Custom classes you wish to pass to the component.
     */
    className: PropTypes.string,
    /**
     * Custom classes you wish to pass to the avatar's image.
     */
    imageClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the avatar's text caption.
     */
    captionClassName: PropTypes.string,
    /**
     * Custom classes you wish to pass to the avatar's container.
     */
    containerClassName: PropTypes.string,
    /**
     * A click callback function.
     */
    onClick: PropTypes.func
};

Avatar.defaultProps = {
    size: 24,
    shape: SHAPES.CIRCLE,
    iconPosition: POSITIONS.BOTTOM_RIGHT,
    showIconRing: true,
    showTitle: true,
    lazyLoad: true,
    autoColor: true,
    icon: null
};

Object.assign(Avatar, {
    SHAPES,
    ONLINE_INDICATOR_POSITIONS: POSITIONS,
    ICON_POSITIONS: POSITIONS
});

export default Avatar;
